/**
 * CreateClubPage - Club Creation Form
 *
 * Single-page form for creating a new club.
 * Rebuilt to match Figma Nightfall Kit design with clean layout.
 *
 * Issue #1046: Refactored to use shared ClubForm component.
 * Reference: docs/design/FIGMA_IMPLEMENTATION_PLAN.md - Phase 7C
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateClub } from '../hooks/useClubs';
import type { ApiError } from '../lib/api';
import { TopBar, ClubForm, type ClubFormData } from '../components';

export default function CreateClubPage() {
  const navigate = useNavigate();
  const createClub = useCreateClub();

  // Store hero image file for upload after club creation
  const [selectedHeroImage, setSelectedHeroImage] = useState<File | null>(null);

  const handleSubmit = async (formData: ClubFormData) => {
    // Create club using mutation - Issue #641: city/state for discovery
    // #635: Virtual clubs skip city/state and use region instead
    // Hybrid clubs have both location and region
    const hasLocation = formData.meeting_mode === 'in_person' || formData.meeting_mode === 'hybrid';
    const hasRegion = formData.meeting_mode === 'virtual' || formData.meeting_mode === 'hybrid';
    createClub.mutate(
      {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        meeting_mode: formData.meeting_mode,
        // Include location fields for in_person and hybrid clubs
        city: hasLocation ? (formData.city.trim() || undefined) : undefined,
        state: hasLocation ? (formData.state || undefined) : undefined,
        location: hasLocation && formData.city.trim() ? formData.city.trim() + (formData.state ? `, ${formData.state}` : '') : undefined,
        // Include region for virtual and hybrid clubs
        region: hasRegion ? formData.region : undefined,
        cover_image_url: undefined, // Hero image will be uploaded after club creation
        // #1648: default to creator's browser tz so in-person event
        // notifications render in the club's local time without requiring
        // the creator to pick a tz from a dropdown.
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      {
        onSuccess: async (response) => {
          const clubId = response.club.id;

          // Upload hero image after club creation if selected
          if (selectedHeroImage && clubId) {
            try {
              const { clubsApi } = await import('../lib/api/clubs');
              await clubsApi.uploadClubHeroImage(clubId, selectedHeroImage);
            } catch (error) {
              console.warn('Failed to upload club hero image:', error);
              // Still navigate even if image upload fails
            }
          }

          navigate(`/clubs/${response.club.slug}`);
        },
      }
    );
  };

  const handleHeroChange = (file: File | null) => {
    setSelectedHeroImage(file);
  };

  return (
    <div className="flex flex-col bg-oro-bg min-h-screen">
      <TopBar
        title="Create Club"
        onBack={() => navigate(-1)}
      />

      <div className="flex-1 overflow-y-auto pt-14">
        <ClubForm
          mode="create"
          onSubmit={handleSubmit}
          onCancel={() => navigate('/clubs')}
          onHeroChange={handleHeroChange}
          isSubmitting={createClub.isPending}
          submitError={(createClub.error as unknown as ApiError)?.message}
        />
      </div>
    </div>
  );
}
